// Photos attached to a story (see the 20260729213000 migration).
//
// The browser does three jobs here that the server cannot do for it:
//
//  1. SHRINK. A phone photo is 3-8MB and 4000px wide; the page shows it at
//     about 600px. Uploading the original would cost the submitter their data
//     and us the storage, and the bucket would reject it anyway at 2MB.
//  2. STRIP METADATA. Drawing a photo onto a canvas and re-encoding keeps the
//     pixels and nothing else, so GPS coordinates, the camera serial and the
//     capture time do not travel with a story someone chose to send
//     anonymously. This is the reason the re-encode is unconditional - a small
//     file still gets redrawn rather than passed through.
//  3. PICK THE PATH. The story's id is generated before the row exists so the
//     files can go into a folder named after it; see uploadStoryPhotos.
//
// None of it is a security control on its own. The bucket enforces the size
// and the mime type, and the storage policies enforce the path shape, so a
// hand-rolled POST gains nothing by skipping this file.

import { supabase } from './supabase'

export const BUCKET = 'story-photos'

/** Matches the cardinality check on lyh_stories.photos. */
export const MAX_PHOTOS = 3

/** Longest edge after downscaling. The story column renders at ~600 CSS px, so
    1600 covers a 2x screen and a reader who opens the image on its own. */
const MAX_DIM = 1600

/** Aim well under the bucket's 2MB ceiling; the quality ladder below stops as
    soon as it gets here. */
const TARGET_BYTES = 900_000

/** What we will read off a file input. Anything else is refused before we spend
    a decode on it; the bucket only accepts what we re-encode to anyway. */
const ACCEPTED = /^image\/(jpeg|png|webp|heic|heif|gif|bmp|avif)$/i

/** Guard against someone handing us a 200MB raw file to decode. Well above any
    phone photo, and the downscale means the upload is nowhere near it. */
const MAX_SOURCE_BYTES = 25 * 1024 * 1024

export interface PreparedPhoto {
  /** The re-encoded JPEG that will be uploaded. */
  blob: Blob
  /** Object URL for the thumbnail. Revoked by the caller on remove/close. */
  previewUrl: string
  /** The original file name, shown so a picker of three photos can tell them
      apart. It is NOT used in the storage path - see uploadStoryPhotos. */
  label: string
}

/** Human-readable reason a file was refused, or null when it is usable. */
export function rejectReason(file: File): string | null {
  if (!ACCEPTED.test(file.type)) {
    return `${file.name} is not an image we can use. JPEG, PNG, WebP or HEIC.`
  }
  if (file.size > MAX_SOURCE_BYTES) {
    return `${file.name} is too big to work with. Try one under 25MB.`
  }
  return null
}

function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  // createImageBitmap handles orientation and is the fast path everywhere it
  // exists; the <img> fallback is for older Safari.
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file, { imageOrientation: 'from-image' })
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('That image could not be opened.'))
    }
    img.src = url
  })
}

function toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('That image could not be read.'))),
      'image/jpeg',
      quality,
    )
  })
}

/**
 * Downscale + re-encode to JPEG. Throws with a message worth showing if the
 * file cannot be decoded at all (a .jpg that is really a PDF, say).
 */
export async function preparePhoto(file: File): Promise<PreparedPhoto> {
  const source = await loadBitmap(file)
  const w = 'width' in source ? source.width : 0
  const h = 'height' in source ? source.height : 0
  if (!w || !h) throw new Error('That image could not be read.')

  const scale = Math.min(1, MAX_DIM / Math.max(w, h))
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(w * scale))
  canvas.height = Math.max(1, Math.round(h * scale))

  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('That image could not be read.')
  // A JPEG has no transparency, so an image that did (a PNG screenshot) would
  // otherwise re-encode with black behind it.
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.drawImage(source as CanvasImageSource, 0, 0, canvas.width, canvas.height)
  if ('close' in source) source.close()

  // Step the quality down until it fits. Three tries, then take what we have -
  // the bucket is the backstop and 0.5 of a 1600px photo is far under 2MB in
  // every case we have seen.
  let blob = await toBlob(canvas, 0.82)
  for (const q of [0.7, 0.6, 0.5]) {
    if (blob.size <= TARGET_BYTES) break
    blob = await toBlob(canvas, q)
  }

  return { blob, previewUrl: URL.createObjectURL(blob), label: file.name }
}

/** A story id, generated client-side so the upload can name its folder before
    the row exists. randomUUID everywhere current; the fallback is for older
    iOS Safari, where crypto.getRandomValues is still available. */
export function newStoryId(): string {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  const b = new Uint8Array(16)
  crypto.getRandomValues(b)
  b[6] = (b[6] & 0x0f) | 0x40
  b[8] = (b[8] & 0x3f) | 0x80
  const hex = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/**
 * Upload the prepared photos into the story's own folder and return the paths
 * to store on the row.
 *
 * The file name is ours, not theirs: an uploaded name can carry a person's name
 * or a phone's location string, and it would then sit in a column that anyone
 * can read off an approved story. Index plus randomness is enough to be unique
 * and says nothing.
 *
 * Every path stays inside `<storyId>/`, which is what the storage INSERT policy
 * and the row's CHECK both require, so a bug here fails loudly rather than
 * writing somewhere it should not.
 */
export async function uploadStoryPhotos(
  storyId: string,
  photos: PreparedPhoto[],
): Promise<{ paths: string[]; error: string | null }> {
  const paths: string[] = []

  for (let i = 0; i < photos.length; i++) {
    const rand = Math.random().toString(36).slice(2, 10)
    const path = `${storyId}/${i}-${rand}.jpg`
    const { error } = await supabase.storage
      .from(BUCKET)
      .upload(path, photos[i].blob, { contentType: 'image/jpeg', upsert: false })
    if (error) return { paths, error: error.message }
    paths.push(path)
  }

  return { paths, error: null }
}

/**
 * Signed URLs for paths we are about to render. Signing goes through the same
 * SELECT policy as any other read, so this returns nothing for a pending
 * story's photo unless the caller is Andrew - which is exactly what makes the
 * public page unable to leak one early.
 *
 * Returns a path→url map with the paths it could sign; a path that could not be
 * signed is simply absent, and the caller renders nothing for it.
 */
export async function signPhotoUrls(
  paths: string[],
  expiresInSeconds = 3600,
): Promise<Record<string, string>> {
  if (paths.length === 0) return {}
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrls(paths, expiresInSeconds)
  if (error || !data) return {}

  const out: Record<string, string> = {}
  for (const row of data) {
    // `path` is typed as string | null and comes back null on the entries the
    // API could not sign.
    if (row.path && row.signedUrl && !row.error) out[row.path] = row.signedUrl
  }
  return out
}

/**
 * Whether the bucket is there at all. Everything photo-related is hidden when
 * it is not, so a project restored without the bucket loses the picker rather
 * than showing one that throws on submit.
 *
 * `list` on a missing bucket errors; on an existing one the read policy simply
 * returns no rows, which is a success. That difference is the whole probe.
 */
export async function photosAvailable(): Promise<boolean> {
  const { error } = await supabase.storage.from(BUCKET).list('', { limit: 1 })
  return !error
}

/** Best-effort cleanup when a story is rejected. Admin-only by policy; a
    failure is logged and otherwise ignored, because a photo left behind is not
    worth failing the rejection over. */
export async function deleteStoryPhotos(paths: string[]): Promise<void> {
  if (paths.length === 0) return
  const { error } = await supabase.storage.from(BUCKET).remove(paths)
  if (error) console.error('remove story photos', error)
}
